import React, { useRef } from "react";
import { RotateCw, Plus } from "lucide-react";

/**
 * MeasureBoxEditor — interactive editor for the AI-detected measurement boxes.
 *
 * The user can:
 *  - drag a box to MOVE it
 *  - drag any of the 4 corner handles to RESIZE it (works while rotated)
 *  - drag the TOP handle to ROTATE it slightly (±45°, for angled photos)
 *  - tap a box to SELECT it (parent controls add/delete)
 *
 * Props:
 *  - photo:    dataURL of the photo (rendered at natural aspect ratio, full width)
 *  - boxes:    [{ box: {x,y,w,h,angle?} (normalized 0-1, angle in degrees), kind }]
 *  - selected: index of the selected box (-1 = none)
 *  - onSelect: (index) => void
 *  - onChange: (nextBoxes) => void
 */

const HANDLES = [
  { key: "nw", x: 0, y: 0 },
  { key: "ne", x: 1, y: 0 },
  { key: "sw", x: 0, y: 1 },
  { key: "se", x: 1, y: 1 },
];
const MIN_SIZE = 0.04;
const MAX_ANGLE = 45;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export function MeasureBoxEditor({ photo, boxes, selected, onSelect, onChange, onAdd }) {
  const wrapRef = useRef(null);
  const dragRef = useRef(null); // { mode, idx, handle, flip, start:{x,y}, orig:{x,y,w,h,angle} }

  const toNorm = (e) => {
    const r = wrapRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const startDrag = (e, idx, mode, handle, flip) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect && onSelect(idx);
    dragRef.current = { mode, idx, handle, flip: !!flip, start: toNorm(e), orig: { angle: 0, ...boxes[idx].box } };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* older browsers */ }
  };

  const onMove = (e) => {
    const d = dragRef.current;
    if (!d || !boxes[d.idx]) return;
    const r = wrapRef.current.getBoundingClientRect();
    const p = toNorm(e);
    let { x, y, w, h, angle = 0 } = d.orig;

    if (d.mode === "rotate") {
      // angle from the box center to the pointer (screen space);
      // the handle sits ABOVE the top edge — or BELOW when the box is near the photo top (flip)
      const cx = (x + w / 2) * r.width;
      const cy = (y + h / 2) * r.height;
      const px = e.clientX - r.left;
      const py = e.clientY - r.top;
      let ang = (Math.atan2(py - cy, px - cx) * 180) / Math.PI + (d.flip ? -90 : 90);
      if (ang > 180) ang -= 360;
      if (ang < -180) ang += 360;
      angle = Math.round(clamp(ang, -MAX_ANGLE, MAX_ANGLE));
    } else if (d.mode === "move") {
      const dx = p.x - d.start.x;
      const dy = p.y - d.start.y;
      x = clamp(x + dx, 0, 1 - w);
      y = clamp(y + dy, 0, 1 - h);
    } else {
      // resize: rotate the pointer delta into the box's local (unrotated) space
      const a = (-(angle || 0) * Math.PI) / 180;
      const dxp = (p.x - d.start.x) * r.width;
      const dyp = (p.y - d.start.y) * r.height;
      const lx = (dxp * Math.cos(a) - dyp * Math.sin(a)) / r.width;
      const ly = (dxp * Math.sin(a) + dyp * Math.cos(a)) / r.height;
      let x2 = x + w;
      let y2 = y + h;
      if (d.handle.includes("w")) x = clamp(x + lx, 0, x2 - MIN_SIZE);
      if (d.handle.includes("e")) x2 = clamp(x2 + lx, x + MIN_SIZE, 1);
      if (d.handle.includes("n")) y = clamp(y + ly, 0, y2 - MIN_SIZE);
      if (d.handle.includes("s")) y2 = clamp(y2 + ly, y + MIN_SIZE, 1);
      w = x2 - x;
      h = y2 - y;
    }
    const next = boxes.slice();
    next[d.idx] = { ...next[d.idx], box: { x, y, w, h, angle } };
    onChange(next);
  };

  const endDrag = () => { dragRef.current = null; };

  return (
    <div
      ref={wrapRef}
      className="relative w-full select-none rounded-xl overflow-hidden border bg-black/5"
      style={{ touchAction: "none" }}
      onPointerMove={onMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={endDrag}
      data-testid="measure-box-editor"
    >
      <img src={photo} alt="" className="w-full h-auto block pointer-events-none" draggable={false} />
      {boxes.map((u, i) => {
        const b = u.box;
        const ang = b.angle || 0;
        const sel = i === selected;
        return (
          <div
            key={i}
            data-testid={`measure-box-${i}`}
            onPointerDown={(e) => startDrag(e, i, "move")}
            className={`absolute border-2 border-dashed cursor-move ${
              sel ? "border-primary bg-primary/10" : "border-amber-400/90 bg-amber-300/5"
            }`}
            style={{
              left: `${b.x * 100}%`,
              top: `${b.y * 100}%`,
              width: `${b.w * 100}%`,
              height: `${b.h * 100}%`,
              transform: ang ? `rotate(${ang}deg)` : undefined,
              transformOrigin: "center center",
              touchAction: "none",
            }}
          >
            <span
              className={`absolute -top-2.5 -left-2.5 h-5 min-w-[20px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center shadow ${
                sel ? "bg-primary text-primary-foreground" : "bg-amber-400 text-black"
              }`}
              data-testid={`measure-box-badge-${i}`}
            >
              {i + 1}{ang ? ` · ${ang}°` : ""}
            </span>
            {sel && (
              <>
                {/* rotation handle — flips below the box when it is near the photo top */}
                {(() => {
                  const flip = b.y < 0.14;
                  return (
                    <>
                      <span className={`absolute left-1/2 ${flip ? "-bottom-7" : "-top-7"} h-7 w-px bg-primary/70 -translate-x-1/2 pointer-events-none`} />
                      <span
                        onPointerDown={(e) => startDrag(e, i, "rotate", undefined, flip)}
                        className={`absolute left-1/2 ${flip ? "-bottom-12" : "-top-12"} -translate-x-1/2 h-7 w-7 rounded-full bg-primary text-primary-foreground border-2 border-white shadow-md flex items-center justify-center`}
                        style={{ touchAction: "none", cursor: "grab" }}
                        data-testid="measure-rotate-handle"
                      >
                        <RotateCw className="h-3.5 w-3.5 pointer-events-none" />
                      </span>
                    </>
                  );
                })()}
                {HANDLES.map((hd) => (
                  <span
                    key={hd.key}
                    onPointerDown={(e) => startDrag(e, i, "resize", hd.key)}
                    className="absolute h-6 w-6 -m-3 rounded-full bg-primary border-2 border-white shadow-md"
                    style={{
                      left: `${hd.x * 100}%`,
                      top: `${hd.y * 100}%`,
                      touchAction: "none",
                      cursor: hd.key === "nw" || hd.key === "se" ? "nwse-resize" : "nesw-resize",
                    }}
                    data-testid={`measure-handle-${hd.key}`}
                  />
                ))}
              </>
            )}
          </div>
        );
      })}
      {/* floating + : add a box when the AI found fewer windows than exist */}
      {onAdd && (
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onAdd(); }}
          className="absolute bottom-2 right-2 h-11 w-11 rounded-full bg-primary text-primary-foreground border-2 border-white shadow-lg flex items-center justify-center active:scale-95 transition-transform"
          data-testid="measure-add-box-fab"
          aria-label="+"
        >
          <Plus className="h-6 w-6 pointer-events-none" />
        </button>
      )}
    </div>
  );
}
